<script setup lang="ts">
import { computed } from 'vue';
import FormatTime from '@/components/FormatTime.vue';
import { useTicketInfoCustomisation } from '../composables/useTicketInfoCustomisation';
import { createBackgroundStyle, createTextStyle } from '@/common/customisation/utils/styleUtils';

interface Props {
  ticket: any;
}

const props = defineProps<Props>();

const { textColor, subTextColor, backgroundColor } = useTicketInfoCustomisation();

const contentWrapperStyle = computed(() => createBackgroundStyle(backgroundColor.value));
const textStyle = computed(() => createTextStyle(textColor.value));
const subTextStyle = computed(() => createTextStyle(subTextColor.value));

const ticketId = computed(() => {
  return props.ticket?.id ? `#${props.ticket.id}` : '';
});

const description = computed(() => {
  return props.ticket?.subject || '';
});

const createdAt = computed(() => {
  return props.ticket?.createdAt ? new Date(props.ticket.createdAt) : null;
});

const lastActivity = computed(() => {
  return props.ticket?.updatedAt ? new Date(props.ticket.updatedAt) : null;
});

const status = computed(() => {
  return props.ticket?.status || '';
});
</script>

<template>
  <div class="theme-font-main ticket-info-content" :style="contentWrapperStyle">
    <div class="info-item">
      <span class="info-label" :style="subTextStyle">Description</span>
      <span class="info-value" :style="textStyle">{{ description }}</span>
    </div>

    <div class="info-item">
      <span class="info-label" :style="subTextStyle">Created</span>
      <span class="info-value" :style="textStyle">
        <FormatTime v-if="createdAt" :date="createdAt" format="d-MMMM-yyyy 'at' HH:mm" />
        <span v-else>—</span>
      </span>
    </div>

    <div class="info-item">
      <span class="info-label" :style="subTextStyle">Last Activity</span>
      <span class="info-value" :style="textStyle">
        <FormatTime v-if="lastActivity" :date="lastActivity" format="d-MMMM-yyyy 'at' HH:mm" />
        <span v-else>—</span>
      </span>
    </div>

    <div class="info-item">
      <span class="info-label" :style="subTextStyle">ID</span>
      <span class="info-value" :style="textStyle">{{ ticketId }}</span>
    </div>

    <div class="info-item">
      <span class="info-label" :style="subTextStyle">Status</span>
      <span class="card-radius status-badge theme-font-h5" :style="textStyle">{{ status }}</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ticket-info-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.info-label {
  flex-shrink: 0;
  min-width: 120px;
}

.info-value {
  text-align: right;
  flex: 1;
  word-break: break-word;
  padding-left: 16px;
}

.status-badge {
  padding: 4px 12px;
  text-transform: capitalize;
}
</style>
